import { ActionIcon, Group, NumberInput, Paper, Text, Tooltip } from '@mantine/core';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import type { ResourceKey } from '@creativitas/shared';
import {
  MONEY_LABEL,
  MONEY_MAX_BILLS_PER_CHANGE,
  MONEY_STEP,
  RESOURCE_LABELS,
} from '@creativitas/shared';
import { MONEY_NOTE_IMAGE, RESOURCE_TOKEN_ICONS } from '@/lib/assets';

type ResourceCounterProps = {
  resource: ResourceKey;
  value: number;
  /** Facilitator-only controls (§38). Omit both to render a read-only counter. */
  onIncrement?: () => void;
  onDecrement?: () => void;
  compact?: boolean;
};

/**
 * One resource of one player. Resources are structured state, never strings
 * scattered through components (§38). The confirmed label for `presence` is
 * «Проявленность».
 */
export function ResourceCounter({
  resource,
  value,
  onIncrement,
  onDecrement,
  compact,
}: ResourceCounterProps) {
  const editable = Boolean(onIncrement || onDecrement);

  return (
    <Paper
      withBorder
      px={compact ? 8 : 'sm'}
      py={compact ? 4 : 6}
      radius="md"
      style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-elevated)' }}
    >
      <Group gap={compact ? 6 : 'xs'} wrap="nowrap">
        {/*
          §8.3: at 22/28 px the crown, the heart and the bag were unreadable on
          the dark board. The glyph is the thing being counted, so it carries
          the size and the number sits next to it.
        */}
        <Tooltip label={RESOURCE_LABELS[resource]}>
          <img
            src={RESOURCE_TOKEN_ICONS[resource]}
            alt=""
            width={compact ? 28 : 38}
            height={compact ? 28 : 38}
          />
        </Tooltip>

        {!compact ? (
          <Text size="sm" c="var(--text-muted)">
            {RESOURCE_LABELS[resource]}
          </Text>
        ) : null}

        {/*
          A facilitator can change a resource from another window, so the number
          gives a small nudge when it lands — that is the state change §62 allows
          animation for. Re-keying on the value restarts it on each change.
        */}
        <motion.span
          key={value}
          initial={{ scale: 1.28, opacity: 0.55 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          style={{ display: 'inline-block' }}
        >
          <Text fw={700} size={compact ? 'sm' : 'md'} ff="var(--font-ui)">
            {value}
          </Text>
        </motion.span>

        {editable ? (
          <Group gap={2} ml={4} wrap="nowrap">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="teal"
              aria-label={`Уменьшить: ${RESOURCE_LABELS[resource]}`}
              onClick={onDecrement}
              disabled={!onDecrement}
            >
              <IconMinus size={14} />
            </ActionIcon>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="teal"
              aria-label={`Увеличить: ${RESOURCE_LABELS[resource]}`}
              onClick={onIncrement}
              disabled={!onIncrement}
            >
              <IconPlus size={14} />
            </ActionIcon>
          </Group>
        ) : null}
      </Group>
    </Paper>
  );
}

type MoneyCounterProps = {
  value: number;
  /** Called with a signed number of 100 000 bills. */
  onChange?: (bills: number) => void;
  /** Drops the word next to the coin so the counter fits a player card row. */
  compact?: boolean;
};

/**
 * Eastern Bazaar balance (§40): numeric state counted in 100 000 bills.
 *
 * The bill is the step, not the limit — a Bazaar card can name «доход миллион»,
 * which is ten bills in one award rather than ten presses, so the facilitator
 * sets how many bills the ± buttons move.
 */
export function MoneyCounter({ value, onChange, compact }: MoneyCounterProps) {
  const editable = Boolean(onChange);
  const [bills, setBills] = useState(1);
  const amount = MONEY_STEP * bills;

  return (
    <Paper
      withBorder
      px={compact ? 8 : 'sm'}
      py={compact ? 4 : 6}
      radius="md"
      style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-gold-soft)' }}
    >
      <Group gap={compact ? 6 : 'xs'} wrap="nowrap">
        {/*
          GAME-33: the bill, not the bag. The bag is one of the three resources
          and lands on the ray; this counter is the numeric balance, and sharing
          the bag's glyph is exactly what made them look like one thing.
        */}
        <Tooltip label={`Баланс, ${MONEY_LABEL}`}>
          <img
            src={MONEY_NOTE_IMAGE}
            alt=""
            width={compact ? 40 : 52}
            height={compact ? 20 : 26}
            style={{ borderRadius: 3, display: 'block' }}
          />
        </Tooltip>
        <motion.span
          key={value}
          initial={{ scale: 1.2, opacity: 0.55 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          style={{ display: 'inline-block' }}
        >
          <Text fw={700} size={compact ? 'sm' : 'md'} ff="var(--font-ui)" c="var(--gold-deep)">
            {value.toLocaleString('ru-RU')}
          </Text>
        </motion.span>
        {!compact ? (
          <Text size="xs" c="var(--text-muted)">
            {MONEY_LABEL}
          </Text>
        ) : null}

        {editable ? (
          <Group gap={2} ml={4} wrap="nowrap">
            {/*
              Without the sign the row is two numbers in a row — the balance and
              the step — and the step reads as the value. «×» says which is which.
            */}
            <Text size="xs" c="var(--text-subtle)" aria-hidden>×</Text>
            <NumberInput
              size="xs"
              w={compact ? 46 : 58}
              min={1}
              max={MONEY_MAX_BILLS_PER_CHANGE}
              step={1}
              hideControls
              value={bills}
              onChange={(next) => setBills(Math.max(1, Math.trunc(Number(next) || 1)))}
              aria-label="Сколько купюр по 100 000"
            />
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gold"
              aria-label={`Убрать ${amount.toLocaleString('ru-RU')}`}
              onClick={() => onChange?.(-bills)}
              disabled={value < amount}
            >
              <IconMinus size={14} />
            </ActionIcon>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gold"
              aria-label={`Добавить ${amount.toLocaleString('ru-RU')}`}
              onClick={() => onChange?.(bills)}
            >
              <IconPlus size={14} />
            </ActionIcon>
          </Group>
        ) : null}
      </Group>
    </Paper>
  );
}
